/**
 * Reference Quantities v2 API — ownable, importable reference parameters.
 */

import { apiClient } from "./client";

export interface ReferenceQuantity {
  id: string;
  path: string;
  parameter: string;
  value: number | null;
  value_json: unknown;
  units: string | null;
  owned_by: string | null;
  approval_status: string;
  change_reason: string | null;
  source: string | null;
  updated_at: string | null;
  is_stale: boolean;
  stale_since: string | null;
  stale_reason: string | null;
  configuration_id: string | null;
  geometry_id: string | null;
  version: number;
  downstream_count: number;
}

export interface RefQuantityUpsert {
  project_id: string;
  branch?: string;
  configuration_id?: string;
  geometry_id?: string;
  parameter: string;
  value: number;
  units?: string;
  owned_by?: string;
  change_reason?: string;
  source?: string;
}

/** Sources that indicate the value was derived from mesh/geometry analysis. */
export const GEOMETRY_DERIVED_SOURCES = new Set([
  "flow360",
  "flow360-import",
  "wing_area_detection",
  "mesh_analysis",
]);

export interface RefQuantityImportItem {
  parameter: string;
  value: number;
  units?: string;
  owned_by?: string;
}

export async function fetchReferenceQuantities(
  projectId: string,
  branch = "main",
  configurationId?: string,
): Promise<ReferenceQuantity[]> {
  const params = new URLSearchParams({ project_id: projectId, branch });
  // Ask the backend for up to 500 ref quantities in a single response.
  // Projects realistically have <50 but the cap avoids silent truncation.
  params.set("limit", "500");
  if (configurationId) params.set("configuration_id", configurationId);
  // Backend paginated PR (#11187) changed the response from a bare list
  // to {total, items: [...]}. Accept both shapes so this code works
  // against any Thread backend version.
  const raw = await apiClient<ReferenceQuantity[] | { total: number; items: ReferenceQuantity[] }>(
    `/reference-quantities?${params}`,
  );
  if (Array.isArray(raw)) return raw;
  return raw?.items ?? [];
}

export function upsertReferenceQuantity(
  body: RefQuantityUpsert,
): Promise<Record<string, unknown>> {
  return apiClient("/reference-quantities", {
    method: "POST",
    body,
  });
}

export function importReferenceQuantities(
  projectId: string,
  branch: string,
  quantities: RefQuantityImportItem[],
  configurationId?: string,
): Promise<{ imported: number; quantities: unknown[] }> {
  return apiClient("/reference-quantities/import", {
    method: "POST",
    body: {
      project_id: projectId,
      branch,
      configuration_id: configurationId,
      quantities,
    },
  });
}

export function deleteReferenceQuantity(
  nodeId: string,
  reason: string,
): Promise<{ status: string; id: string }> {
  return apiClient(`/reference-quantities/${nodeId}`, {
    method: "DELETE",
    body: { reason },
  });
}

export function approveParameterChange(
  nodeId: string,
  comment?: string,
): Promise<{ status: string; id: string }> {
  return apiClient(`/reference-quantities/${nodeId}/approve`, {
    method: "POST",
    body: comment ? { comment } : {},
  });
}

export function rejectParameterChange(
  nodeId: string,
  comment?: string,
): Promise<{ status: string; id: string }> {
  return apiClient(`/reference-quantities/${nodeId}/reject`, {
    method: "POST",
    body: comment ? { comment } : {},
  });
}

export function transferOwnership(
  nodeId: string,
  ownedBy: string,
): Promise<Record<string, unknown>> {
  return apiClient(`/reference-quantities/${nodeId}/owner`, {
    method: "PUT",
    body: { owned_by: ownedBy },
  });
}

export interface RefQuantityHistoryEntry {
  value: number | null;
  units: string | null;
  operation: string;
  changed_by: string;
  changed_at: string;
  commit_id: string | null;
}

export function fetchRefQuantityHistory(
  nodeId: string,
  projectId: string,
  branch = "main",
  limit = 50,
): Promise<RefQuantityHistoryEntry[]> {
  const params = new URLSearchParams({
    project_id: projectId,
    branch,
    limit: String(limit),
  });
  return apiClient<RefQuantityHistoryEntry[]>(
    `/reference-quantities/${nodeId}/history?${params}`,
  );
}
